import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { reqPost, reqGet } from '../../../service';
import { formatNumber } from '../../../utils/formatNumber';
import DrillDownView from './DrillDownView';
import {
  splitGroupsBySection,
  getSectionKey,
  SECTION_META,
  SECTION_CUSTOM,
  SECTION_TEMPLATE,
} from '../../utils/groupSection';

// ============== 데이터/도메인 헬퍼 ==============

const GROUP_DESCRIPTIONS = {
  infraSetting: '개발에 필요한 인프라, 플랫폼, 웹엔진 등 기본 환경',
  function: '시스템 기능 구현 및 비즈니스 로직 개발',
};

function getGroupDescription(group) {
  if (!group) return '';
  if (GROUP_DESCRIPTIONS[group.std_key]) return GROUP_DESCRIPTIONS[group.std_key];
  return getSectionKey(group.std_key) === SECTION_CUSTOM
    ? '맞춤 개발 그룹'
    : `${group.std_value} 템플릿`;
}

// ============== 스타일 ==============

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${p => p.theme.bgOverlay};
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  background: ${p => p.theme.bgCard};
  width: 95vw;
  max-width: 1280px;
  height: 92vh;
  border-radius: ${p => p.theme.radiusLg};
  box-shadow: ${p => p.theme.shadowLg};
  border: 1px solid ${p => p.theme.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media screen and (max-width: 1024px) {
    width: 100vw;
    height: 100vh;
    max-width: none;
    border-radius: 0;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${p => p.theme.accent} 0%, #6d4ba2 100%);
  color: #fff;
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: 480px) {
    font-size: 14px;
    gap: 6px;
  }
`;

const TitleIcon = styled.span`
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const CloseX = styled.button`
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: ${p => p.theme.transition};

  &:hover {
    background: rgba(255,255,255,0.3);
  }
`;

const InfoBar = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 12px;
  padding: 10px 24px;
  border-bottom: 1px solid ${p => p.theme.border};

  @media screen and (max-width: 768px) {
    padding: 10px 14px;
  }
`;

const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 320px 1fr 360px;
  gap: 0;
  overflow: hidden;
  background: ${p => p.theme.bg};

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-auto-rows: min-content;
    overflow-y: auto;
  }
`;

const Column = styled.section`
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bg};
  overflow: hidden;

  &:last-child {
    border-right: none;
  }

  @media screen and (max-width: 1024px) {
    border-right: none;
    border-bottom: 1px solid ${p => p.theme.border};
    overflow: visible;
  }
`;

const ColumnHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
`;

const ColumnTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountBadge = styled.span`
  font-size: 11px;
  color: ${p => p.theme.accent};
  background: ${p => p.theme.accentLight};
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
`;

const PrimaryAddButton = styled.button`
  background: ${p => p.theme.accent};
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.accentHover};
  }
`;

const OutlineAddButton = styled.button`
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.accent};
  border: 1px solid ${p => p.theme.accent};
  padding: 6px 12px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: ${p => p.theme.accentLight};
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  font-size: 12px;
  box-sizing: border-box;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

// 그룹 / 카테고리 카드 공용 스타일
// $depth: 0 = 최상위 (강조), >=1 = 하위 (회색 차분한 톤)
const SelectableCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${p => p.$depth > 0 ? '8px 12px' : '12px'};
  background: ${p => {
    if (p.$dragOver) return p.theme.accentLight;
    if (p.$selected) return p.theme.bgSelected;
    if (p.$depth > 0) return p.theme.bgInput;
    return p.theme.bgCard;
  }};
  border: ${p => p.$dragOver ? '2px dashed' : '1px solid'} ${p => {
    if (p.$dragOver) return p.theme.accent;
    if (p.$selected) return p.theme.borderSelected;
    if (p.$depth > 0) return 'transparent';
    return p.theme.border;
  }};
  border-radius: ${p => p.theme.radiusSm};
  cursor: ${p => p.$dragging ? 'grabbing' : 'grab'};
  margin-bottom: 6px;
  opacity: ${p => p.$dragging ? 0.5 : 1};
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:hover {
    border-color: ${p => p.theme.accent};
  }
`;

const FolderIcon = styled.span`
  width: ${p => p.$depth > 0 ? '22px' : '28px'};
  height: ${p => p.$depth > 0 ? '22px' : '28px'};
  border-radius: 6px;
  background: ${p => {
    if (p.$selected) return p.theme.accent;
    if (p.$depth > 0) return p.theme.bgCard;
    return p.theme.bgInput;
  }};
  color: ${p => {
    if (p.$selected) return '#fff';
    if (p.$depth > 0) return p.theme.textMuted;
    return p.theme.textSecondary;
  }};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardName = styled.div`
  font-size: ${p => p.$depth > 0 ? '12px' : '13px'};
  font-weight: ${p => p.$depth > 0 ? 500 : 600};
  color: ${p => p.$depth > 0 ? p.theme.textSecondary : p.theme.text};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDesc = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TreeNodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px;
  background: ${p => p.$selected ? p.theme.bgSelected : p.theme.bgCard};
  border: 1px solid ${p => p.$selected ? p.theme.borderSelected : p.theme.border};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  margin-bottom: 6px;
  transition: ${p => p.theme.transition};

  &:hover {
    border-color: ${p => p.theme.accent};
  }
`;

const TreeCaret = styled.button`
  background: transparent;
  border: none;
  width: 16px;
  height: 16px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.textMuted};
  cursor: pointer;
  font-size: 10px;
  flex-shrink: 0;
  visibility: ${p => p.$hidden ? 'hidden' : 'visible'};

  &:hover {
    color: ${p => p.theme.accent};
  }
`;

const StatusBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  ${p => p.$variant === 'start' && `
    background: #d1fae5;
    color: #047857;
  `}
  ${p => p.$variant === 'orphan' && `
    background: #fef3c7;
    color: #b45309;
  `}
`;

const InfoStrip = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 11px;
  padding: 8px 12px;
  border-radius: ${p => p.theme.radiusSm};
  margin-bottom: 10px;
  line-height: 1.5;

  strong { color: ${p => p.theme.text}; }

  span.warn { color: #b45309; font-weight: 600; }
`;

const NodeMenu = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme.textMuted};
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 16px;
  line-height: 1;

  &:hover {
    background: ${p => p.theme.bgHover};
    color: ${p => p.theme.text};
  }
`;

const CardCount = styled.span`
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  font-weight: 600;
  flex-shrink: 0;
`;

const Chevron = styled.span`
  color: ${p => p.theme.textMuted};
  font-size: 14px;
  flex-shrink: 0;
`;

const FullAddButton = styled.button`
  width: 100%;
  padding: 10px;
  border: 1px dashed ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.textSecondary};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: ${p => p.theme.transition};

  &:hover {
    border-color: ${p => p.theme.accent};
    color: ${p => p.theme.accent};
    background: ${p => p.theme.accentLight};
  }
`;

const DragHint = styled.div`
  text-align: center;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  padding: 12px;
  border-top: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
`;

const SectionGroup = styled.div`
  margin-bottom: 6px;
`;

const SectionGroupLabel = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  font-weight: 600;
  padding: 8px 4px 6px;
`;

// 우측 상세 패널
const DetailHeaderActions = styled.div`
  display: flex;
  gap: 6px;
`;

const SmallActionButton = styled.button`
  border: 1px solid ${p => p.$danger ? p.theme.error : p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.$danger ? p.theme.error : p.theme.textSecondary};
  padding: 5px 10px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: ${p => p.theme.transition};

  &:hover:not(:disabled) {
    background: ${p => p.$danger ? '#fee2e2' : p.theme.bgHover};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DetailCard = styled.div`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  margin-bottom: 12px;
  overflow: hidden;
`;

const DetailCardTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.theme.text};
  padding: 12px 14px;
  border-bottom: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgInput};
`;

const DetailGrid = styled.div`
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 80px 1fr;
  row-gap: 8px;
  column-gap: 12px;
  font-size: 12px;
`;

const DetailKey = styled.span`
  color: ${p => p.theme.textMuted};
`;

const DetailValue = styled.span`
  color: ${p => p.theme.text};
  word-break: break-word;
`;

const ParentLink = styled.span`
  display: inline-block;
  color: ${p => p.theme.accent};
  background: ${p => p.theme.accentLight};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
`;

const EmptyArea = styled.div`
  padding: 24px 14px;
  text-align: center;
  color: ${p => p.theme.textMuted};
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const EmptyIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: ${p => p.theme.bgInput};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.textMuted};

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-top: 1px solid ${p => p.theme.border};
  font-size: 12px;
  color: ${p => p.theme.text};
`;

const ItemRowName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
`;

const ItemRowPrice = styled.span`
  font-weight: 700;
  color: ${p => p.theme.accent};
  white-space: nowrap;
`;

const Footer = styled.div`
  border-top: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  padding: 12px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const FooterButton = styled.button`
  padding: 8px 18px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.textSecondary};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.bgHover};
    color: ${p => p.theme.text};
  }
`;

// 모달
const ModalWrap = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${p => p.theme.bgCard};
  padding: 22px;
  border-radius: ${p => p.theme.radius};
  box-shadow: ${p => p.theme.shadowLg};
  z-index: 3000;
  min-width: 360px;
  max-width: 480px;
  border: 1px solid ${p => p.theme.border};

  @media screen and (max-width: 480px) {
    width: calc(100vw - 24px);
    min-width: 0;
    padding: 16px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: ${p => p.theme.text};
  font-size: 12px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  box-sizing: border-box;
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const FormTextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  min-height: 70px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  box-sizing: border-box;
  background-color: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const ModalButton = styled.button`
  padding: 8px 16px;
  border: ${p => p.$primary ? 'none' : `1px solid ${p.theme.border}`};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: ${p => p.theme.transition};
  background: ${p => p.$primary ? p.theme.accent : p.theme.bgCard};
  color: ${p => p.$primary ? '#fff' : p.theme.text};

  &:hover {
    background: ${p => p.$primary ? p.theme.accentHover : p.theme.bgHover};
  }
`;

// SVG 아이콘
const IconFolder = () => (
  <svg viewBox="0 0 24 24"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" /></svg>
);
const IconPencil = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);
const IconLayers = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 3l9 5-9 5-9-5 9-5z" />
    <path d="M3 13l9 5 9-5" />
    <path d="M3 18l9 5 9-5" />
  </svg>
);
const IconInbox = () => (
  <svg viewBox="0 0 24 24">
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

// ============== 컴포넌트 ==============

const AdminDashboard = ({ isOpen, onClose }) => {
  const [groups, setGroups] = useState([]);
  const [categoriesByGroup, setCategoriesByGroup] = useState({});
  const [itemsByCategory, setItemsByCategory] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [collapsedNodes, setCollapsedNodes] = useState({}); // 트리 접힘 상태 (default 펼침)
  // 아이템(하위 항목) 편집 모달 — next_category 설정 가능
  const [itemEditModal, setItemEditModal] = useState(null);
  const [itemEditForm, setItemEditForm] = useState({});
  const [itemEditCategoryOptions, setItemEditCategoryOptions] = useState([]);
  // 드래그-드롭으로 트리에 자식 연결
  const [draggedCategoryName, setDraggedCategoryName] = useState(null);
  const [dragOverCategoryName, setDragOverCategoryName] = useState(null);
  // 드롭 시 부모 아이템 선택 모달
  const [linkPromptModal, setLinkPromptModal] = useState(null);

  const loadGroups = useCallback(() => {
    reqGet('/api/groupList', {}, (data) => {
      if (data && data.length > 0) {
        setGroups(data);
        setSelectedGroup(prev => prev || data[0]);
      } else {
        setGroups([]);
      }
    });
  }, []);

  const loadCategories = useCallback((groupKey) => {
    reqGet('/api/CategoryList', { group_id: groupKey }, (data) => {
      setCategoriesByGroup(prev => ({ ...prev, [groupKey]: data || [] }));
    });
  }, []);

  const loadItems = useCallback((groupKey, categoryName) => {
    const key = `${groupKey}::${categoryName}`;
    reqGet('/api/ItemList', { group_id: groupKey, category: categoryName }, (data) => {
      setItemsByCategory(prev => ({ ...prev, [key]: data || [] }));
    });
  }, []);

  // 모달 열림 시 데이터 로드
  useEffect(() => {
    if (isOpen) loadGroups();
  }, [isOpen, loadGroups]);

  // 그룹 선택되면 카테고리 로드
  useEffect(() => {
    if (selectedGroup && !categoriesByGroup[selectedGroup.std_key]) {
      loadCategories(selectedGroup.std_key);
    }
  }, [selectedGroup, categoriesByGroup, loadCategories]);

  // 카테고리 선택되면 아이템 로드
  useEffect(() => {
    if (selectedCategory && selectedGroup) {
      const key = `${selectedGroup.std_key}::${selectedCategory.main_category}`;
      if (!itemsByCategory[key]) {
        loadItems(selectedGroup.std_key, selectedCategory.main_category);
      }
    }
  }, [selectedCategory, selectedGroup, itemsByCategory, loadItems]);

  // 트리 구조 계산을 위해 그룹 카테고리들의 아이템을 미리 모두 로드
  useEffect(() => {
    if (!selectedGroup) return;
    const cats = categoriesByGroup[selectedGroup.std_key];
    if (!cats) return;
    cats.forEach(cat => {
      const key = `${selectedGroup.std_key}::${cat.main_category}`;
      if (!itemsByCategory[key]) {
        loadItems(selectedGroup.std_key, cat.main_category);
      }
    });
  }, [selectedGroup, categoriesByGroup, itemsByCategory, loadItems]);

  // 아이템 편집 모달이 열려있는 동안 categoriesByGroup이 갱신되면 dropdown 옵션도 sync
  useEffect(() => {
    if (!itemEditModal || !selectedGroup) return;
    const cats = categoriesByGroup[selectedGroup.std_key] || [];
    setItemEditCategoryOptions(cats.map(c => ({
      value: c.main_category,
      label: c.main_category,
    })));
  }, [itemEditModal, selectedGroup, categoriesByGroup]);

  const groupSections = useMemo(() => splitGroupsBySection(groups), [groups]);

  const currentCategories = useMemo(() => {
    if (!selectedGroup) return [];
    const list = categoriesByGroup[selectedGroup.std_key] || [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter(c =>
      (c.main_category || '').toLowerCase().includes(q) ||
      (c.info || '').toLowerCase().includes(q)
    );
  }, [selectedGroup, categoriesByGroup, search]);

  // next_category 링크로 카테고리 트리 빌드
  // 알고리즘: wizard 시작점에서 BFS로 자식 배정 우선 (wizard 체인이 canonical 부모)
  // 그 외 카테고리는 fallback으로 첫 발견된 부모 배정
  const categoryTree = useMemo(() => {
    if (!selectedGroup) return [];
    const cats = categoriesByGroup[selectedGroup.std_key] || [];
    if (cats.length === 0) return [];

    const forcedRootName = selectedGroup.std_key === 'infraSetting'
      ? '인프라'
      : cats[0]?.main_category;

    const byName = {};
    cats.forEach(c => { byName[c.main_category] = c; });

    // 각 카테고리의 next_category 들 수집 (모든 후보)
    const nextOf = {}; // parentName -> Set<childName>
    cats.forEach(c => {
      const itemKey = `${selectedGroup.std_key}::${c.main_category}`;
      const items = itemsByCategory[itemKey] || [];
      const set = new Set();
      items.forEach(it => {
        if (it.next_category && byName[it.next_category]) set.add(it.next_category);
      });
      nextOf[c.main_category] = set;
    });

    const parentOf = {};
    const childrenOf = {};
    const claim = (parent, child) => {
      if (parent === child) return;
      if (parentOf[child]) return; // 이미 부모 있음
      if (child === forcedRootName) return; // wizard 시작점은 자식 안 됨
      parentOf[child] = parent;
      if (!childrenOf[parent]) childrenOf[parent] = [];
      childrenOf[parent].push(child);
    };

    // 1단계 (우선): wizard 시작점부터 BFS로 자식 배정
    if (forcedRootName && byName[forcedRootName]) {
      const queue = [forcedRootName];
      const visited = new Set([forcedRootName]);
      while (queue.length > 0) {
        const cur = queue.shift();
        const children = nextOf[cur] || new Set();
        children.forEach(child => {
          if (visited.has(child)) return;
          claim(cur, child);
          visited.add(child);
          queue.push(child);
        });
      }
    }

    // 2단계 (fallback): 아직 부모 없는 카테고리에 대해 다른 카테고리의 next_category로부터 배정
    cats.forEach(c => {
      const children = nextOf[c.main_category] || new Set();
      children.forEach(child => claim(c.main_category, child));
    });

    // 루트: 부모 없는 카테고리들
    let roots = cats.filter(c => !parentOf[c.main_category]);

    // wizard 시작점 강제 첫 위치
    if (forcedRootName && byName[forcedRootName]) {
      roots = [
        byName[forcedRootName],
        ...roots.filter(c => c.main_category !== forcedRootName),
      ];
    }

    const buildNode = (cat, depth, visited) => {
      if (visited.has(cat.main_category)) return null;
      const next = new Set(visited);
      next.add(cat.main_category);
      const childNames = childrenOf[cat.main_category] || [];
      return {
        ...cat,
        depth,
        children: childNames
          .map(n => byName[n])
          .filter(Boolean)
          .map(c => buildNode(c, depth + 1, next))
          .filter(Boolean),
      };
    };

    return roots.map(c => buildNode(c, 0, new Set())).filter(Boolean);
  }, [selectedGroup, categoriesByGroup, itemsByCategory]);

  // 트리에서 (부모 카테고리, 어떤 아이템 다음에 연결되었는지) + 자식들 lookup
  const treeRelations = useMemo(() => {
    if (!selectedGroup) return { parentMap: {}, parentItemMap: {}, childrenMap: {}, allParentsMap: {} };
    const cats = categoriesByGroup[selectedGroup.std_key] || [];
    const forcedRootName = selectedGroup.std_key === 'infraSetting'
      ? '인프라'
      : cats[0]?.main_category;
    const parentMap = {};      // childName -> first canonical parentName
    const parentItemMap = {};  // childName -> the item_name in canonical parent
    const childrenMap = {};    // parentName -> [{childName, viaItemName}]
    const allParentsMap = {};  // childName -> [{parentName, viaItemName}] — 모든 부모 (multi-parent)
    cats.forEach(c => {
      const itemKey = `${selectedGroup.std_key}::${c.main_category}`;
      const items = itemsByCategory[itemKey] || [];
      items.forEach(it => {
        if (!it.next_category) return;
        // wizard 시작점은 누구의 canonical 자식도 되지 않음
        if (it.next_category !== forcedRootName && !parentMap[it.next_category]) {
          parentMap[it.next_category] = c.main_category;
          parentItemMap[it.next_category] = it.item_name;
        }
        if (!childrenMap[c.main_category]) childrenMap[c.main_category] = [];
        childrenMap[c.main_category].push({
          childName: it.next_category,
          viaItemName: it.item_name,
        });
        // 모든 부모 후보 기록 (multi-parent)
        if (!allParentsMap[it.next_category]) allParentsMap[it.next_category] = [];
        allParentsMap[it.next_category].push({
          parentName: c.main_category,
          viaItemName: it.item_name,
        });
      });
    });
    return { parentMap, parentItemMap, childrenMap, allParentsMap };
  }, [selectedGroup, categoriesByGroup, itemsByCategory]);

  // wizard 시작점 결정: infraSetting은 '인프라' 하드코딩, 그 외 그룹은 카테고리 리스트의 첫 행
  const wizardRoot = useMemo(() => {
    if (!selectedGroup) return null;
    if (selectedGroup.std_key === 'infraSetting') return '인프라';
    const cats = categoriesByGroup[selectedGroup.std_key] || [];
    return cats[0]?.main_category || null;
  }, [selectedGroup, categoriesByGroup]);

  // wizard 시작점에서 next_category 따라가며 도달 가능한 카테고리들의 집합
  const wizardReachable = useMemo(() => {
    const reachable = new Set();
    if (!wizardRoot) return reachable;
    reachable.add(wizardRoot);
    const queue = [wizardRoot];
    while (queue.length > 0) {
      const current = queue.shift();
      const children = treeRelations.childrenMap[current] || [];
      children.forEach(c => {
        if (!reachable.has(c.childName)) {
          reachable.add(c.childName);
          queue.push(c.childName);
        }
      });
    }
    return reachable;
  }, [wizardRoot, treeRelations]);

  const toggleCollapse = (name) => {
    setCollapsedNodes(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const currentItems = useMemo(() => {
    if (!selectedGroup || !selectedCategory) return [];
    const key = `${selectedGroup.std_key}::${selectedCategory.main_category}`;
    return itemsByCategory[key] || [];
  }, [selectedGroup, selectedCategory, itemsByCategory]);

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setSelectedCategory(null);
    setSearch('');
  };

  const openAddGroupModal = () => {
    setEditModal({ type: 'group' });
    setEditForm({ name: '', description: '' });
  };
  const openAddCategoryModal = () => {
    if (!selectedGroup) return;
    setEditModal({ type: 'category', parentNode: selectedGroup });
    setEditForm({ name: '', description: '', categoryType: 'S' });
  };
  const openAddItemModal = () => {
    if (!selectedGroup || !selectedCategory) return;
    setEditModal({
      type: 'item',
      parentNode: { groupKey: selectedGroup.std_key, name: selectedCategory.main_category },
    });
    setEditForm({ name: '', description: '', price: '' });
  };
  const openEditCategoryModal = () => {
    if (!selectedCategory) return;
    setEditModal({
      type: 'category',
      isEdit: true,
      parentNode: selectedGroup,
      original: selectedCategory,
    });
    setEditForm({
      name: selectedCategory.main_category,
      description: selectedCategory.info || '',
      categoryType: selectedCategory.category_type || selectedCategory.category_Type || 'S',
    });
  };

  const handleSave = () => {
    const { type, parentNode } = editModal;
    if (!editForm.name?.trim()) {
      alert('이름을 입력해주세요.');
      return;
    }
    let endpoint = '';
    let data = {};
    if (type === 'group') {
      endpoint = 'api/addGroup';
      data = { 그룹: { value: editForm.name, label: editForm.name } };
    } else if (type === 'category' && editModal.isEdit) {
      // 수정: cascading rename API 호출
      endpoint = 'api/UpdateCategory';
      data = {
        group_id: parentNode.std_key,
        old_name: editModal.original?.main_category,
        new_name: editForm.name,
        info: editForm.description || '',
        category_type: editForm.categoryType || 'S',
      };
    } else if (type === 'category') {
      endpoint = 'api/groupAdd';
      data = {
        그룹: { value: parentNode.std_key, label: parentNode.std_value },
        카테고리: editForm.name,
        설명: editForm.description || '',
        카테고리타입: editForm.categoryType || 'S',
      };
    } else if (type === 'item') {
      if (!editForm.price) {
        alert('가격을 입력해주세요.');
        return;
      }
      endpoint = 'api/itemAdd';
      data = {
        그룹: { value: parentNode.groupKey, label: parentNode.groupKey },
        카테고리: { value: parentNode.name, label: parentNode.name },
        제목: editForm.name,
        가격: editForm.price,
        설명: editForm.description || '',
      };
    }
    reqPost(endpoint, data, () => {
      alert('저장되었습니다.');
      setEditModal(null);
      if (type === 'group') {
        loadGroups();
      } else if (type === 'category') {
        const groupKey = parentNode.std_key;
        loadCategories(groupKey);
        // 이름 변경됐을 수 있으니 그룹 모든 카테고리 아이템 재로드 (next_category 참조 갱신 반영)
        if (editModal.isEdit) {
          const cats = categoriesByGroup[groupKey] || [];
          cats.forEach(cat => loadItems(groupKey, cat.main_category));
          // 새 이름으로 selectedCategory 갱신
          if (editForm.name && editModal.original?.main_category !== editForm.name) {
            // loadCategories 후 새 이름으로 setSelectedCategory 하기 위해 약간 지연
            setTimeout(() => {
              setSelectedCategory(prev => prev ? { ...prev, main_category: editForm.name, info: editForm.description, category_type: editForm.categoryType } : prev);
            }, 200);
          }
        }
      } else if (type === 'item') {
        loadItems(parentNode.groupKey, parentNode.name);
      }
    }, (err) => {
      console.error(err);
      alert('저장에 실패했습니다.');
    });
  };

  const handleDeleteCategory = () => {
    if (!selectedCategory || !selectedGroup) return;
    const itemKey = `${selectedGroup.std_key}::${selectedCategory.main_category}`;
    const itemCount = (itemsByCategory[itemKey] || []).length;
    const msg = itemCount > 0
      ? `"${selectedCategory.main_category}" 카테고리를 삭제합니다.\n` +
        `이 카테고리에 속한 ${itemCount}개의 하위 항목과,\n` +
        `이 카테고리를 가리키는 다른 항목들의 연결도 함께 끊깁니다.\n\n계속하시겠습니까?`
      : `"${selectedCategory.main_category}" 카테고리를 삭제하시겠습니까?`;
    if (!window.confirm(msg)) return;
    reqPost('/api/DeleteCategory', {
      group_id: selectedGroup.std_key,
      main_category: selectedCategory.main_category,
    }, () => {
      alert('카테고리가 삭제되었습니다.');
      const deletedName = selectedCategory.main_category;
      setSelectedCategory(null);
      // 그룹 카테고리 + 모든 아이템 재로드
      loadCategories(selectedGroup.std_key);
      const cats = (categoriesByGroup[selectedGroup.std_key] || []).filter(c => c.main_category !== deletedName);
      cats.forEach(cat => loadItems(selectedGroup.std_key, cat.main_category));
    }, () => {
      alert('카테고리 삭제에 실패했습니다.');
    });
  };

  const handleDeleteGroup = () => {
    if (!selectedGroup) return;
    if (!window.confirm(
      `"${selectedGroup.std_value}" 대주제를 통째로 삭제합니다.\n` +
      `이 그룹에 속한 모든 카테고리와 항목이 함께 삭제됩니다.\n\n계속하시겠습니까?`
    )) return;
    reqPost('/api/DeleteGroup', {
      group_id: selectedGroup.std_key,
    }, () => {
      alert('대주제가 삭제되었습니다.');
      setSelectedGroup(null);
      setSelectedCategory(null);
      loadGroups();
    }, () => {
      alert('대주제 삭제에 실패했습니다.');
    });
  };

  const handleDeleteItem = (item) => {
    if (!selectedGroup || !selectedCategory) return;
    if (!window.confirm(`"${item.item_name}" 항목을 삭제하시겠습니까?`)) return;
    reqPost('/api/DeleteCard', {
      group_id: selectedGroup.std_key,
      category: selectedCategory.main_category,
      item_name: item.item_name,
    }, () => {
      alert('삭제되었습니다.');
      loadItems(selectedGroup.std_key, selectedCategory.main_category);
    });
  };

  const openItemEditModal = (item) => {
    if (!selectedGroup || !selectedCategory) return;
    setItemEditForm({
      name: item.item_name,
      price: item.item_price,
      description: item.item_description || '',
      next_category: item.next_category || '',
    });
    setItemEditModal({ original: item });
    // 카테고리 옵션은 admin이 보유한 최신 state 에서 즉시 derive (옵션 0개라도 일단 모달 오픈)
    const groupKey = selectedGroup.std_key;
    const cats = categoriesByGroup[groupKey] || [];
    setItemEditCategoryOptions(cats.map(c => ({
      value: c.main_category,
      label: c.main_category,
    })));
    // 그래도 안전망으로 DB에서 한번 더 가져와서 동기화 (admin state가 stale 한 경우 대비)
    loadCategories(groupKey);
  };

  const handleItemEditSave = () => {
    const original = itemEditModal?.original;
    if (!original || !selectedGroup || !selectedCategory) return;
    reqPost('/api/UpdatePrice', {
      item_price: itemEditForm.price,
      category: selectedCategory.main_category,
      item_name: original.item_name,
      group_id: selectedGroup.std_key,
      item_description: itemEditForm.description,
    }, () => {
      const finishSave = () => {
        alert('저장되었습니다.');
        setItemEditModal(null);
        loadItems(selectedGroup.std_key, selectedCategory.main_category);
      };
      // next_category 변경 시 setNext 호출
      if ((itemEditForm.next_category || '') !== (original.next_category || '')) {
        reqPost('/api/setNext', {
          next: itemEditForm.next_category || '',
          category: selectedCategory.main_category,
          item_name: original.item_name,
        }, finishSave);
      } else {
        finishSave();
      }
    });
  };

  // 드래그-드롭 핸들러
  const handleNodeDragStart = (e, node) => {
    setDraggedCategoryName(node.main_category);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleNodeDragEnd = () => {
    setDraggedCategoryName(null);
    setDragOverCategoryName(null);
  };
  const handleNodeDragOver = (e, node) => {
    if (!draggedCategoryName || draggedCategoryName === node.main_category) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverCategoryName !== node.main_category) {
      setDragOverCategoryName(node.main_category);
    }
  };
  const handleNodeDragLeave = (node) => {
    if (dragOverCategoryName === node.main_category) {
      setDragOverCategoryName(null);
    }
  };
  const handleNodeDrop = (e, parentNode) => {
    e.preventDefault();
    const childName = draggedCategoryName;
    setDraggedCategoryName(null);
    setDragOverCategoryName(null);
    if (!childName || childName === parentNode.main_category) return;

    // 자기 자손에게 드롭하는 사이클 방지
    const isDescendant = (n) => {
      if (!n.children) return false;
      for (const c of n.children) {
        if (c.main_category === parentNode.main_category) return true;
        if (isDescendant(c)) return true;
      }
      return false;
    };
    const draggedNode = (function findInTree(nodes) {
      for (const n of nodes) {
        if (n.main_category === childName) return n;
        if (n.children) {
          const found = findInTree(n.children);
          if (found) return found;
        }
      }
      return null;
    })(categoryTree);
    if (draggedNode && isDescendant(draggedNode)) {
      alert('자기 자신의 하위 노드로는 이동할 수 없습니다.');
      return;
    }

    // 부모의 아이템 중 어떤 항목에 연결할지 선택받기
    const parentItemKey = `${selectedGroup.std_key}::${parentNode.main_category}`;
    const parentItems = itemsByCategory[parentItemKey] || [];
    if (parentItems.length === 0) {
      alert(`"${parentNode.main_category}"에 항목이 없어 연결할 수 없습니다.\n먼저 항목을 추가해주세요.`);
      return;
    }
    setLinkPromptModal({
      parentName: parentNode.main_category,
      childName,
      items: parentItems,
    });
  };

  const handleConfirmLink = (selectedItem) => {
    const { parentName, childName } = linkPromptModal;
    reqPost('/api/setNext', {
      next: childName,
      category: parentName,
      item_name: selectedItem.item_name,
    }, () => {
      alert(`연결되었습니다.\n${parentName} > ${selectedItem.item_name} → ${childName}`);
      setLinkPromptModal(null);
      // 모든 카테고리 아이템 재로드 (트리 재계산)
      const cats = categoriesByGroup[selectedGroup.std_key] || [];
      cats.forEach(cat => loadItems(selectedGroup.std_key, cat.main_category));
    });
  };

  if (!isOpen) return null;

  // 좌측 그룹 카드 렌더
  const renderGroupCard = (group) => {
    const isSelected = selectedGroup?.std_key === group.std_key;
    const cats = categoriesByGroup[group.std_key];
    const count = cats ? cats.length : '·';
    return (
      <SelectableCard
        key={group.std_key}
        $selected={isSelected}
        onClick={() => handleSelectGroup(group)}
      >
        <FolderIcon $selected={isSelected}><IconFolder /></FolderIcon>
        <CardBody>
          <CardName>{group.std_value}</CardName>
          <CardDesc>{getGroupDescription(group)}</CardDesc>
        </CardBody>
        <CardCount>{count}</CardCount>
      </SelectableCard>
    );
  };

  // 우측 상세 - 선택된 카테고리 정보
  const detailNode = selectedCategory;
  const detailItemCount = currentItems.length;

  return (
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>
            <TitleIcon><IconLayers /></TitleIcon>
            트리 관리자
          </Title>
          <CloseX onClick={onClose}>×</CloseX>
        </Header>

        <InfoBar>대주제를 선택하면 해당 그룹의 하위 트리를 관리할 수 있습니다.</InfoBar>

        <Body>
          {/* 좌측 - 대주제 */}
          <Column>
            <ColumnHeader>
              <ColumnTitle>대주제 선택</ColumnTitle>
            </ColumnHeader>
            <ScrollArea>
              {groupSections[SECTION_CUSTOM].length > 0 && (
                <SectionGroup>
                  <SectionGroupLabel>{SECTION_META[SECTION_CUSTOM].title}</SectionGroupLabel>
                  {groupSections[SECTION_CUSTOM].map(renderGroupCard)}
                </SectionGroup>
              )}
              {groupSections[SECTION_TEMPLATE].length > 0 && (
                <SectionGroup>
                  <SectionGroupLabel>{SECTION_META[SECTION_TEMPLATE].title}</SectionGroupLabel>
                  {groupSections[SECTION_TEMPLATE].map(renderGroupCard)}
                </SectionGroup>
              )}
              <FullAddButton onClick={openAddGroupModal}>+ 새 대주제 추가</FullAddButton>
            </ScrollArea>
          </Column>

          {/* 중앙 - 하위 트리 (카테고리) */}
          <Column>
            <ColumnHeader>
              <ColumnTitle>
                선택된 대주제의 진행 단계
                <CountBadge>{(categoriesByGroup[selectedGroup?.std_key] || []).length}개 카테고리</CountBadge>
              </ColumnTitle>
              <OutlineAddButton
                onClick={openAddCategoryModal}
                disabled={!selectedGroup}
              >+ 새 트리 추가</OutlineAddButton>
            </ColumnHeader>
            <div style={{ padding: '12px 12px 0' }}>
              <SearchInput
                placeholder="카테고리 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {selectedGroup && categoryTree.length > 0 && (() => {
                const cats = categoriesByGroup[selectedGroup.std_key] || [];
                const wizardRootExists = wizardRoot && cats.some(c => c.main_category === wizardRoot);
                const orphanRoots = categoryTree.filter(n => !wizardReachable.has(n.main_category));
                // wizardRoot 자체가 cats에 없으면 reachable에 카운트하지 않음
                const reachableCount = wizardRootExists
                  ? Array.from(wizardReachable).filter(n => cats.some(c => c.main_category === n)).length
                  : 0;
                const totalCount = cats.length;
                if (!wizardRootExists) {
                  return (
                    <InfoStrip style={{ background: '#fef3c7', color: '#92400e' }}>
                      ⚠ Wizard 시작점 카테고리 <strong>"{wizardRoot}"</strong>가 DB에 없습니다.
                      견적서 메인 화면이 정상 동작하지 않을 수 있어요. 새 카테고리로 추가하거나, 기존 카테고리 이름을 "{wizardRoot}"로 변경해주세요.
                    </InfoStrip>
                  );
                }
                return (
                  <InfoStrip>
                    견적서 wizard는 <strong>{wizardRoot}</strong>에서 시작해 항목 선택 시 next_category 따라 진행 ·{' '}
                    {totalCount}개 중 <strong>{reachableCount}개</strong> 도달 가능
                    {orphanRoots.length > 0 && (
                      <>, <span className="warn">{orphanRoots.length}개 고아</span> (wizard 진입 불가 — 다른 항목의 next_category로 연결하거나 삭제해주세요)</>
                    )}
                  </InfoStrip>
                );
              })()}
            </div>
            {/* 검색 중: 평면 매칭 리스트, 그 외: 흐름도 */}
            {search ? (
              <ScrollArea>
                {currentCategories.length === 0 ? (
                  <EmptyArea>
                    <EmptyIcon><IconInbox /></EmptyIcon>
                    <div>검색 결과가 없습니다.</div>
                  </EmptyArea>
                ) : (
                  currentCategories.map(cat => {
                    const isSelected = selectedCategory?.main_category === cat.main_category;
                    const itemKey = `${selectedGroup.std_key}::${cat.main_category}`;
                    const itemCount = (itemsByCategory[itemKey] || []).length;
                    return (
                      <SelectableCard
                        key={cat.main_category}
                        $selected={isSelected}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        <FolderIcon $selected={isSelected}><IconLayers /></FolderIcon>
                        <CardBody>
                          <CardName>{cat.main_category}</CardName>
                          <CardDesc>{cat.info || '설명 없음'}</CardDesc>
                        </CardBody>
                        <CardCount>{itemCount}</CardCount>
                      </SelectableCard>
                    );
                  })
                )}
              </ScrollArea>
            ) : (categoriesByGroup[selectedGroup?.std_key] || []).length === 0 ? (
              <ScrollArea>
                <EmptyArea>
                  <EmptyIcon><IconInbox /></EmptyIcon>
                  <div>
                    {selectedGroup
                      ? '하위 항목이 없습니다.'
                      : '왼쪽에서 대주제를 선택하세요.'}
                  </div>
                </EmptyArea>
              </ScrollArea>
            ) : (
              <DrillDownView
                categories={categoriesByGroup[selectedGroup.std_key] || []}
                itemsByCategory={itemsByCategory}
                groupKey={selectedGroup.std_key}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
                onLinkNext={openItemEditModal}
                wizardRoot={wizardRoot}
                wizardReachable={wizardReachable}
              />
            )}
            <DragHint>항목 카드를 클릭하면 다음 카테고리로 이동, 상단 빵부스러기로 되돌아가기</DragHint>
          </Column>

          {/* 우측 - 상세 정보 */}
          <Column>
            <ColumnHeader>
              <ColumnTitle>현재 트리 상세</ColumnTitle>
              <DetailHeaderActions>
                <SmallActionButton
                  disabled={!detailNode}
                  onClick={openEditCategoryModal}
                  title="현재 트리 정보 수정 — 이름 변경 시 연결된 next_category 참조도 자동으로 같이 갱신됩니다"
                >수정</SmallActionButton>
                <SmallActionButton
                  $danger
                  disabled={!detailNode}
                  onClick={handleDeleteCategory}
                  title="현재 트리 삭제"
                >삭제</SmallActionButton>
              </DetailHeaderActions>
            </ColumnHeader>
            <ScrollArea>
              {!detailNode ? (
                <EmptyArea style={{ paddingTop: 60 }}>
                  <EmptyIcon><IconInbox /></EmptyIcon>
                  <div>중앙에서 하위 트리를 선택하세요.</div>
                </EmptyArea>
              ) : (
                <>
                  <DetailCard>
                    <DetailCardTitle>기본 정보</DetailCardTitle>
                    <DetailGrid>
                      <DetailKey>이름</DetailKey>
                      <DetailValue>{detailNode.main_category}</DetailValue>
                      <DetailKey>설명</DetailKey>
                      <DetailValue>{detailNode.info || '-'}</DetailValue>
                      <DetailKey>상위 그룹</DetailKey>
                      <DetailValue>
                        <ParentLink>{selectedGroup?.std_value}</ParentLink>
                      </DetailValue>
                      <DetailKey>항목 수</DetailKey>
                      <DetailValue>{detailItemCount}</DetailValue>
                      <DetailKey>선택 타입</DetailKey>
                      <DetailValue>
                        {(detailNode.category_type || detailNode.category_Type) === 'M'
                          ? '멀티 선택'
                          : '싱글 선택'}
                      </DetailValue>
                    </DetailGrid>
                  </DetailCard>

                  {/* 트리 부모 / 자식 관계 */}
                  {(() => {
                    const parentName = treeRelations.parentMap[detailNode.main_category];
                    const parentItem = treeRelations.parentItemMap[detailNode.main_category];
                    const children = treeRelations.childrenMap[detailNode.main_category] || [];
                    const findCatByName = (name) =>
                      (categoriesByGroup[selectedGroup.std_key] || []).find(c => c.main_category === name);

                    // wizard 시작점부터 현재 노드까지 path trace
                    const buildPath = (name) => {
                      const path = [name];
                      let cursor = name;
                      const visited = new Set([name]);
                      while (treeRelations.parentMap[cursor]) {
                        const parent = treeRelations.parentMap[cursor];
                        if (visited.has(parent)) break;
                        visited.add(parent);
                        path.unshift(parent);
                        cursor = parent;
                      }
                      return path;
                    };
                    const path = buildPath(detailNode.main_category);
                    const isOnWizardChain = wizardReachable.has(detailNode.main_category);

                    return (
                      <DetailCard>
                        <DetailCardTitle>트리 관계</DetailCardTitle>
                        <DetailGrid>
                          <DetailKey>Wizard 상태</DetailKey>
                          <DetailValue>
                            {detailNode.main_category === wizardRoot ? (
                              <StatusBadge $variant="start">시작점</StatusBadge>
                            ) : isOnWizardChain ? (
                              <span style={{ fontSize: 12, color: '#047857' }}>도달 가능</span>
                            ) : (
                              <StatusBadge $variant="orphan">고아 (진입 불가)</StatusBadge>
                            )}
                          </DetailValue>
                          <DetailKey>경로</DetailKey>
                          <DetailValue>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                              {path.map((p, i) => (
                                <React.Fragment key={p}>
                                  {i > 0 && <span style={{ color: '#94a3b8', fontSize: 11 }}>›</span>}
                                  <span
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      const c = findCatByName(p);
                                      if (c) setSelectedCategory(c);
                                    }}
                                  >
                                    <ParentLink>{p}</ParentLink>
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          </DetailValue>
                          <DetailKey>진입 가능한<br/>모든 부모</DetailKey>
                          <DetailValue>
                            {(() => {
                              const allParents = treeRelations.allParentsMap[detailNode.main_category] || [];
                              if (allParents.length === 0) {
                                return <span style={{ color: '#94a3b8', fontSize: 12 }}>없음 (트리 루트 또는 고아)</span>;
                              }
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {allParents.map((p, i) => (
                                    <span
                                      key={`${p.parentName}-${p.viaItemName}-${i}`}
                                      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                                      onClick={() => {
                                        const c = findCatByName(p.parentName);
                                        if (c) setSelectedCategory(c);
                                      }}
                                    >
                                      <ParentLink>{p.parentName}</ParentLink>
                                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                        ({p.viaItemName} 다음)
                                      </span>
                                    </span>
                                  ))}
                                  {allParents.length > 1 && (
                                    <span style={{ fontSize: 11, color: '#0f766e', marginTop: 2 }}>
                                      · 총 {allParents.length}개 경로로 진입 가능
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </DetailValue>
                          <DetailKey>하위 트리</DetailKey>
                          <DetailValue>
                            {children.length === 0 ? (
                              <span style={{ color: '#94a3b8', fontSize: 12 }}>연결된 하위 트리 없음</span>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {children.map((c, i) => (
                                  <span
                                    key={i}
                                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    onClick={() => {
                                      const ch = findCatByName(c.childName);
                                      if (ch) setSelectedCategory(ch);
                                    }}
                                  >
                                    <ParentLink>{c.childName}</ParentLink>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                      ({c.viaItemName} 선택 시)
                                    </span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </DetailValue>
                        </DetailGrid>
                      </DetailCard>
                    );
                  })()}

                  <DetailCard>
                    <DetailCardTitle>하위 항목 ({detailItemCount})</DetailCardTitle>
                    {detailItemCount === 0 ? (
                      <EmptyArea>
                        <EmptyIcon><IconInbox /></EmptyIcon>
                        <div>등록된 하위 항목이 없습니다.</div>
                        <PrimaryAddButton onClick={openAddItemModal}>+ 하위 항목 추가</PrimaryAddButton>
                      </EmptyArea>
                    ) : (
                      <>
                        {currentItems.map((item, idx) => (
                          <ItemRow key={idx}>
                            <ItemRowName>{item.item_name}</ItemRowName>
                            <ItemRowPrice>{formatNumber(item.item_price)}만원</ItemRowPrice>
                            <SmallActionButton
                              style={{ marginLeft: 8, padding: '3px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => openItemEditModal(item)}
                              title="편집 (가격/설명/다음 카테고리)"
                            ><IconPencil />수정</SmallActionButton>
                            <SmallActionButton
                              $danger
                              style={{ marginLeft: 4, padding: '3px 8px', fontSize: 11 }}
                              onClick={() => handleDeleteItem(item)}
                            >삭제</SmallActionButton>
                          </ItemRow>
                        ))}
                        <div style={{ padding: 12, borderTop: '1px solid var(--border, #e2e8f0)' }}>
                          <PrimaryAddButton onClick={openAddItemModal}>+ 하위 항목 추가</PrimaryAddButton>
                        </div>
                      </>
                    )}
                  </DetailCard>

                </>
              )}
            </ScrollArea>
          </Column>
        </Body>

        <Footer>
          <FooterButton onClick={onClose}>닫기</FooterButton>
        </Footer>

        {editModal && (
          <ModalWrap onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {editModal.type === 'group' && (editModal.isEdit ? '대주제 수정' : '새 대주제 추가')}
              {editModal.type === 'category' && (editModal.isEdit ? '현재 트리 수정' : '새 트리 추가')}
              {editModal.type === 'item' && (editModal.isEdit ? '하위 항목 수정' : '하위 항목 추가')}
            </ModalTitle>
            <FormGroup>
              <FormLabel>이름 *</FormLabel>
              <FormInput
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="이름을 입력하세요"
                autoFocus
              />
            </FormGroup>
            {editModal.type === 'item' && (
              <FormGroup>
                <FormLabel>가격 (만원) *</FormLabel>
                <FormInput
                  type="text"
                  value={editForm.price ? formatNumber(editForm.price) : ''}
                  onChange={(e) => {
                    const v = e.target.value.replace(/,/g, '');
                    if (!isNaN(v)) setEditForm({ ...editForm, price: v });
                  }}
                  placeholder="가격을 입력하세요"
                />
              </FormGroup>
            )}
            {editModal.type === 'category' && (
              <FormGroup>
                <FormLabel>선택 타입 *</FormLabel>
                <FormSelect
                  value={editForm.categoryType || 'S'}
                  onChange={(e) => setEditForm({ ...editForm, categoryType: e.target.value })}
                >
                  <option value="S">싱글 선택 (하나만 선택 가능)</option>
                  <option value="M">멀티 선택 (여러 개 선택 가능)</option>
                </FormSelect>
              </FormGroup>
            )}
            <FormGroup>
              <FormLabel>설명</FormLabel>
              <FormTextArea
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="설명을 입력하세요"
              />
            </FormGroup>
            <ModalButtons>
              <ModalButton onClick={() => setEditModal(null)}>취소</ModalButton>
              <ModalButton $primary onClick={handleSave}>
                {editModal.isEdit ? '수정' : '추가'}
              </ModalButton>
            </ModalButtons>
          </ModalWrap>
        )}

        {itemEditModal && (
          <ModalWrap onClick={(e) => e.stopPropagation()}>
            <ModalTitle>하위 항목 편집 — {itemEditModal.original.item_name}</ModalTitle>
            <FormGroup>
              <FormLabel>이름</FormLabel>
              <FormInput value={itemEditForm.name || ''} disabled />
            </FormGroup>
            <FormGroup>
              <FormLabel>가격 (만원) *</FormLabel>
              <FormInput
                type="text"
                value={itemEditForm.price ? formatNumber(itemEditForm.price) : ''}
                onChange={(e) => {
                  const v = e.target.value.replace(/,/g, '');
                  if (!isNaN(v)) setItemEditForm({ ...itemEditForm, price: v });
                }}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>설명</FormLabel>
              <FormTextArea
                value={itemEditForm.description || ''}
                onChange={(e) => setItemEditForm({ ...itemEditForm, description: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>다음 카테고리 (이 항목 선택 시 표시될 하위 트리)</FormLabel>
              <FormSelect
                value={itemEditForm.next_category || ''}
                onChange={(e) => setItemEditForm({ ...itemEditForm, next_category: e.target.value })}
              >
                <option value="">— 없음 —</option>
                {itemEditCategoryOptions
                  .filter(c => c.value !== selectedCategory?.main_category)
                  .map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
              </FormSelect>
            </FormGroup>
            <ModalButtons>
              <ModalButton onClick={() => setItemEditModal(null)}>취소</ModalButton>
              <ModalButton $primary onClick={handleItemEditSave}>저장</ModalButton>
            </ModalButtons>
          </ModalWrap>
        )}

        {linkPromptModal && (
          <ModalWrap onClick={(e) => e.stopPropagation()}>
            <ModalTitle>하위 트리 연결</ModalTitle>
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>
              "<strong>{linkPromptModal.parentName}</strong>"의 어떤 항목 다음에<br />
              "<strong>{linkPromptModal.childName}</strong>"을(를) 표시할까요?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
              {linkPromptModal.items.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => handleConfirmLink(item)}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    background: '#fff',
                    borderRadius: 8,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: 13,
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; }}
                >
                  <span>{item.item_name}</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {item.next_category ? `현재: ${item.next_category}` : '연결 없음'}
                  </span>
                </button>
              ))}
            </div>
            <ModalButtons>
              <ModalButton onClick={() => setLinkPromptModal(null)}>취소</ModalButton>
            </ModalButtons>
          </ModalWrap>
        )}
      </Container>
    </Overlay>
  );
};

export default AdminDashboard;
